/**
 * TanStack Query の QueryClient と Provider を生成するファクトリ。
 *
 * - 既定の `staleTime` / `retry` をここで一元設定する
 * - ブラウザでは QueryClient を 1 つにシングルトン化し、HMR module reload を跨いでも
 *   キャッシュが残るように `globalThis` に保持する (module scope の変数だと
 *   Bun --hot の HMR で再評価されてリセットされる)
 * - 返り値の `QueryProvider` を各アプリの Provider 階層に差し込んで使う
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 既定で 30 秒は再フェッチしない。RPC ごとに override 可。
        staleTime: 30 * 1000,
        // 一時的なネットワーク揺らぎを 1 回だけリトライ。
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * @param key QueryClient を保持する `globalThis` のキー。同一オリジンで複数アプリが
 *   衝突しないよう、アプリごとに名前空間付きの文字列を渡す
 *   (例: `"__viajero_manager_QueryClient__"`)。
 */
export function createQueryProvider(key: string) {
  const g = globalThis as unknown as Record<string, QueryClient | undefined>;

  function getQueryClient(): QueryClient {
    if (typeof window === "undefined") {
      return makeQueryClient();
    }
    return (g[key] ??= makeQueryClient());
  }

  function QueryProvider({ children }: { children: ReactNode }) {
    return <QueryClientProvider client={getQueryClient()}>{children}</QueryClientProvider>;
  }

  return { getQueryClient, QueryProvider };
}
